import { Callout } from '@humanforce/mango-ui-react';
import type { PayRunApprovalDto } from './payRunsApiTypes';
import { PayRunInlineCallout } from './PayRunInlineCallout';

interface PayRunApprovalWarningsProps {
  approval: PayRunApprovalDto;
  error: string | null;
}

/**
 * What survives of the pre-PRR-402 "Payment approvals" card on the payroll user's screen.
 *
 * The V3 design has no approvals card: progress now lives in the Approve step of the processing
 * timeline, and an approver acts in the approver view. But the card was also the only place three
 * warnings appeared, and the design has no home for them because its frames only show healthy
 * states. Dropping them would let an operator act against a superseded or stale approval context,
 * so they are kept here, verbatim, as plain callouts.
 *
 * Deliberately NOT carried over: the per-requirement stream cards (the timeline shows progress),
 * the Approve/Reject controls and their rejection dialog (the approver view owns decisions), the
 * calculation-hash line, and the "Ready to submit" notice, which the V3 banner already states.
 */
export const PayRunApprovalWarnings = ({ approval, error }: PayRunApprovalWarningsProps) => {
  const superseded = approval.isSuperseded;
  const stale = approval.isCalculationStale && !superseded;
  if (!error && !superseded && !stale) return null;

  return (
    <div className="mgo-stack mgo-gap-m" data-testid="pay-run-approval-warnings">
      {error && <PayRunInlineCallout tone="critical" title="Approval action failed" body={error} />}
      {superseded && (
        <PayRunInlineCallout
          tone="caution"
          title="Approval history superseded"
          body="These decisions are retained for audit but cannot authorize the current Pay Run."
        />
      )}
      {stale && (
        <Callout tone="caution" title="Approval unavailable — the calculation has changed">
          This approval context was frozen against calculation {approval.calculationHash}, which is no longer the Pay Run&apos;s
          current calculation. Approvals can only authorize the calculation they were frozen against, so no approval,
          rejection or submission can be accepted against this context. Use Unlock to Compliance to re-run readiness and
          submit a fresh approval context. Existing approvals will not carry forward and every sign-off must be given again.
        </Callout>
      )}
    </div>
  );
};
